/**
 * Embed-iframe host bridge — Phase 1B-I follow-up (2026-05-30).
 *
 * When the SPA renders inside `@nexbasira/embed`'s iframe, this hook
 * consumes the `{ source: "nb-host", type: ... }` postMessage envelopes
 * the host page sends (via `widget.startRecording()`, `.pauseRecording()`,
 * etc.) and routes them to the same React Query mutations the in-room
 * RecordingControl uses — so audit + bus envelopes + segment metadata
 * + chat system messages fire identically regardless of who triggered
 * the action.
 *
 * Outbound counterpart: emits `{ source: "nb-iframe", type: ... }`
 * envelopes back to `window.parent.postMessage(..., "*")` so the host
 * page's `EmbedOptions.onReady` / `.onSessionComplete` / `.onError`
 * callbacks fire. We don't know the host's origin in advance (the host
 * minted the iframe URL but could be on any domain), so `targetOrigin`
 * is `*`; the host SDK filters inbound by their expected iframe origin,
 * not the other way around.
 *
 * Security: we accept INBOUND commands only when:
 *   • the message arrived from `window.parent` (not from an arbitrary
 *     embedded iframe inside our own page), AND
 *   • `event.data.source === "nb-host"`.
 * Origin checks aren't sufficient since we don't know the host origin
 * in advance.
 *
 * Scope of v1 (this commit):
 *   • Inbound: start/stop/pause/resume recording.
 *   • Outbound: `ready` on mount, `session-completed` on session end,
 *     `error` on a command failure.
 *
 * Commands documented in the SDK but NOT yet routed:
 *   • request-snapshot, open/close-whiteboard, switch-camera,
 *     mute/unmute, end-session
 *   These need LiveKit room context + the CommandBus, which lives
 *   deeper in the component tree. Tracked as a follow-up.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { ApiPaths, api } from "@/lib/api";
import { useRecordingStore } from "@/lib/recording";
import { useSessionBus } from "@/lib/sessionBus";
import type { Session } from "@/lib/types";


export type HostCommand =
  | { source: "nb-host"; type: "request-snapshot" }
  | { source: "nb-host"; type: "open-whiteboard" }
  | { source: "nb-host"; type: "close-whiteboard" }
  | { source: "nb-host"; type: "switch-camera" }
  | { source: "nb-host"; type: "mute" }
  | { source: "nb-host"; type: "unmute" }
  | { source: "nb-host"; type: "end-session" }
  | { source: "nb-host"; type: "start-recording" }
  | { source: "nb-host"; type: "stop-recording" }
  | { source: "nb-host"; type: "pause-recording" }
  | { source: "nb-host"; type: "resume-recording" };


export type IframeEvent =
  | { source: "nb-iframe"; type: "ready" }
  | { source: "nb-iframe"; type: "session-joined"; sessionId: string }
  | { source: "nb-iframe"; type: "session-completed"; sessionId: string }
  | {
      source: "nb-iframe";
      type: "error";
      message: string;
      code?: string;
    };


/** Fire a `nb-iframe` envelope at the parent window. No-op when the
 * SPA isn't embedded (window.parent === window). targetOrigin is "*"
 * because we don't know the host origin in advance; the host SDK
 * filters inbound by their expected iframe origin. */
export function postToHost(evt: IframeEvent): void {
  try {
    if (window.parent === window) return;
    window.parent.postMessage(evt, "*");
  } catch {
    // postMessage rarely throws but defensively swallow — host
    // pages should never have their session disrupted by our
    // outbound emission.
  }
}


// ---- LiveKit-context command handlers --------------------------------
// Commands that need <LiveKitRoom> context (request-snapshot,
// open/close-whiteboard, switch-camera, mute/unmute) can't be routed
// from SessionPage's outer hook — that hook lives ABOVE the LiveKit
// provider. We expose a tiny pub/sub so an inner component (mounted
// in OperatorRoom) can register its handler; the outer hook forwards
// unrouted commands to it.

type LivekitHandler = (cmd: HostCommand) => boolean;
const livekitHandlers = new Set<LivekitHandler>();

/** Register a LiveKit-context handler. The handler returns true when
 * it handled the command (the dispatch loop stops), or false / void
 * to let the next handler try. Returns an unsubscribe fn for useEffect
 * cleanup. */
export function registerLivekitCommandHandler(
  fn: LivekitHandler,
): () => void {
  livekitHandlers.add(fn);
  return () => {
    livekitHandlers.delete(fn);
  };
}

function dispatchToLivekit(cmd: HostCommand): boolean {
  for (const h of livekitHandlers) {
    if (h(cmd)) return true;
  }
  return false;
}


type HostBridgeOptions = {
  sessionId: string;
  /** Optional handler for commands we don't route ourselves — lets
   *  OperatorRoom plug in camera/mute/snapshot once those land. */
  onUnhandled?: (cmd: HostCommand) => void;
};


/** Mount inside SessionPage (or any session-scoped component). Wires
 * the inbound listener once per session id; mutations live in the
 * hook scope so each command shares the same React Query cache the
 * in-room buttons use. */
export function useEmbedHostBridge({
  sessionId,
  onUnhandled,
}: HostBridgeOptions): void {
  const wbBus = useSessionBus((s) => s.bus);
  const qc = useQueryClient();
  // Invalidate the session query so the right-sidebar status pill +
  // recording controls re-render after the bridge changes state.
  // Used by every recording-lifecycle mutation's onSuccess.
  const invalidateSession = () => {
    void qc.invalidateQueries({ queryKey: ["session", sessionId] });
  };
  // Stash the latest onUnhandled in a ref so the message listener
  // always sees the freshest closure without re-subscribing.
  const unhandledRef = useRef(onUnhandled);
  unhandledRef.current = onUnhandled;

  const start = useMutation({
    mutationFn: async () => {
      await api.post(ApiPaths.sessionConsent(sessionId), {
        kind: "recording",
        decision: "granted",
        note: "host-initiated via embed",
      });
      return (
        await api.post<{ egress_id: string }>(
          ApiPaths.sessionRecordingStart(sessionId),
        )
      ).data;
    },
    onSuccess: () => {
      const at = Date.now();
      useRecordingStore.getState().set(true, at);
      invalidateSession();
      void wbBus
        ?.send({
          topic: "system",
          type: "recording.started",
          payload: { at },
        })
        .catch(() => {});
    },
  });
  const stop = useMutation({
    mutationFn: async () =>
      (await api.post<Session>(ApiPaths.sessionRecordingStop(sessionId))).data,
    onSuccess: () => {
      useRecordingStore.getState().reset();
      invalidateSession();
      void wbBus
        ?.send({ topic: "system", type: "recording.stopped", payload: {} })
        .catch(() => {});
    },
  });
  const pause = useMutation({
    mutationFn: async () =>
      (await api.post<Session>(ApiPaths.sessionRecordingPause(sessionId)))
        .data,
    onSuccess: () => {
      const at = Date.now();
      useRecordingStore.getState().pause(at);
      invalidateSession();
      void wbBus
        ?.send({ topic: "system", type: "recording.paused", payload: { at } })
        .catch(() => {});
    },
  });
  const resume = useMutation({
    mutationFn: async () =>
      (await api.post<Session>(ApiPaths.sessionRecordingResume(sessionId)))
        .data,
    onSuccess: () => {
      const at = Date.now();
      useRecordingStore.getState().resume(at);
      invalidateSession();
      void wbBus
        ?.send({
          topic: "system",
          type: "recording.resumed",
          payload: { at },
        })
        .catch(() => {});
    },
  });
  const endSession = useMutation({
    mutationFn: async () => {
      // Fire the session.ended bus broadcast non-blocking so a
      // slow/stalled LiveKit data channel can't hang the HTTP /end
      // POST. The broadcast is best-effort context for the field's
      // EOF handler; the durable end signal is the backend's
      // delete_room call after /end succeeds.
      void wbBus
        ?.send({
          topic: "system",
          type: "session.ended",
          payload: { reason: "host-initiated via embed" },
        })
        .catch(() => {});
      return (await api.post<Session>(ApiPaths.sessionEnd(sessionId))).data;
    },
    onSuccess: () => {
      postToHost({
        source: "nb-iframe",
        type: "session-completed",
        sessionId,
      });
      invalidateSession();
    },
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.parent === window) return; // not embedded; skip listener

    const onMessage = (event: MessageEvent): void => {
      // Source check — only accept commands from the embedding parent.
      if (event.source !== window.parent) return;
      const data = event.data as { source?: string; type?: string } | null;
      if (!data || data.source !== "nb-host" || typeof data.type !== "string") {
        return;
      }
      const cmd = data as HostCommand;
      switch (cmd.type) {
        case "start-recording":
          start.mutate(undefined, {
            onError: (e) =>
              postToHost({
                source: "nb-iframe",
                type: "error",
                message: (e as Error).message,
                code: "start-recording-failed",
              }),
          });
          return;
        case "stop-recording":
          stop.mutate(undefined, {
            onError: (e) =>
              postToHost({
                source: "nb-iframe",
                type: "error",
                message: (e as Error).message,
                code: "stop-recording-failed",
              }),
          });
          return;
        case "pause-recording":
          pause.mutate(undefined, {
            onError: (e) =>
              postToHost({
                source: "nb-iframe",
                type: "error",
                message: (e as Error).message,
                code: "pause-recording-failed",
              }),
          });
          return;
        case "resume-recording":
          resume.mutate(undefined, {
            onError: (e) =>
              postToHost({
                source: "nb-iframe",
                type: "error",
                message: (e as Error).message,
                code: "resume-recording-failed",
              }),
          });
          return;
        case "end-session":
          endSession.mutate(undefined, {
            onError: (e) =>
              postToHost({
                source: "nb-iframe",
                type: "error",
                message: (e as Error).message,
                code: "end-session-failed",
              }),
          });
          return;
        default:
          // LiveKit-context commands (request-snapshot, open/close-
          // whiteboard, switch-camera, mute/unmute) route through the
          // inner pub/sub to a handler mounted inside <LiveKitRoom>.
          // If no handler claims it, fall back to onUnhandled.
          if (!dispatchToLivekit(cmd)) {
            unhandledRef.current?.(cmd);
          }
          return;
      }
    };

    window.addEventListener("message", onMessage);
    // Announce iframe readiness so the host SDK's onReady fires.
    postToHost({ source: "nb-iframe", type: "ready" });

    return () => {
      window.removeEventListener("message", onMessage);
    };
    // Intentionally only re-subscribe on sessionId change. The
    // mutation closures read live state from the store + react-query,
    // so a stale closure here would still post to the right session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
}
